import { Button } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";

interface ExpandableProps {
  children: React.ReactNode;
  maxLines?: number;
  size?: "1" | "2" | "3" | "4";
  buttonText?: React.ReactNode;
  /**
   * When true, an expanded block can be re-collapsed via a "Show less" button.
   * Defaults to false so existing one-way callers are unchanged.
   */
  allowCollapse?: boolean;
  /** Label for the collapse button when `allowCollapse` is set. */
  collapseButtonText?: React.ReactNode;
  /**
   * Prefix for the toggle buttons' `data-testid`s: `${testId}-show-more` and
   * `${testId}-show-less`. Falls back to `expandable` so every render carries a
   * stable testid.
   */
  testId?: string;
  onExpand?: (
    content: React.ReactNode,
  ) => Promise<React.ReactNode> | React.ReactNode | void;
}

export function Expandable({
  children,
  maxLines = 4,
  size = "3",
  buttonText = "Click to expand...",
  allowCollapse = false,
  collapseButtonText = "Show less",
  testId,
  onExpand,
}: ExpandableProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  // Latch overflow: while expanded the block uses `pre-wrap`, where
  // scrollHeight === clientHeight, so live measurement can't be trusted to
  // decide whether a "Show less" toggle belongs. Remember it overflowed.
  const [everTruncated, setEverTruncated] = useState(false);
  const [content, setContent] = useState(children);
  const testIdPrefix = testId ?? "expandable";

  // Reads only refs, so its identity is stable and both effects below can
  // depend on it without re-running.
  const checkTruncation = useCallback(() => {
    const node = containerRef.current;
    if (node) {
      const truncated = node.scrollHeight > node.clientHeight;
      setIsTruncated(truncated);
      if (truncated) setEverTruncated(true);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [children, isExpanded, checkTruncation]);

  // Bound once per instance instead of re-bound on every render. `scrollHeight`
  // forces a synchronous layout read and tables render many of these rows, so a
  // resize burst is coalesced down to one read per frame.
  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(checkTruncation);
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, [checkTruncation]);

  const handleClick = async () => {
    setIsExpanded(true);

    if (typeof onExpand === "function") {
      let result = onExpand(content);
      if (result instanceof Promise) {
        result = await result;
      }

      if (result !== undefined) {
        setContent(result);
      }
    }
  };

  return (
    <>
      <div
        ref={containerRef}
        style={
          isExpanded
            ? { whiteSpace: "pre-wrap", minWidth: "0" }
            : {
                // Tailwind can't dynamically create classes from variables, so we need to use inline styles
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: String(maxLines),
                overflow: "hidden",
              }
        }
      >
        {content}
      </div>
      {!isExpanded && isTruncated && (
        <Button
          onClick={handleClick}
          variant="ghost"
          size={size}
          aria-expanded={false}
          data-testid={`${testIdPrefix}-show-more`}
          className="cursor-pointer rounded border-none text-left text-xs font-medium text-puntt-accent-12 transition-colors hover:text-puntt-accent-10 active:text-puntt-accent-11"
        >
          {buttonText}
        </Button>
      )}
      {isExpanded && allowCollapse && everTruncated && (
        <Button
          onClick={() => setIsExpanded(false)}
          variant="ghost"
          size={size}
          aria-expanded={true}
          data-testid={`${testIdPrefix}-show-less`}
          className="cursor-pointer rounded border-none text-left text-xs font-medium text-puntt-accent-12 transition-colors hover:text-puntt-accent-10 active:text-puntt-accent-11"
        >
          {collapseButtonText}
        </Button>
      )}
    </>
  );
}
